import React from "react";
import { cn } from "@/lib/utils";
import { AlertCircle, Mail, CheckCircle } from "lucide-react";

interface MetricsPanelProps {
  bounceRate: string;
  complaintRate: string;
  deliveryRate: string;
  timeRange?: string;
  className?: string;
}

export function MetricsPanel({
  bounceRate,
  complaintRate,
  deliveryRate,
  timeRange = "Last 30 days",
  className,
}: MetricsPanelProps) {
  // Parse rates to numbers for comparison
  const bounceRateNum = parseFloat(bounceRate);
  const complaintRateNum = parseFloat(complaintRate);
  const deliveryRateNum = parseFloat(deliveryRate);

  // Check if metrics are within AWS SES requirements
  const isBounceRateGood = bounceRateNum < 2.0;
  const isComplaintRateGood = complaintRateNum < 0.1;
  const isDeliveryRateGood = deliveryRateNum > 98.0;

  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-3 gap-4", className)}>
      {/* Bounce Rate Panel */}
      <div className="bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-400" />
              <h3 className="text-sm font-medium text-gray-200">Bounce Rate</h3>
            </div>
            <span className="text-xs text-gray-400">{timeRange}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-3xl font-bold text-white">{bounceRate}%</span>
            <div className="mt-2 flex items-center">
              <span 
                className={cn(
                  "text-xs",
                  isBounceRateGood ? "text-green-400" : "text-red-400"
                )}
              >
                Target: &lt;2% (AWS Requirement)
              </span>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div 
                className={cn(
                  "h-2 rounded-full",
                  isBounceRateGood ? "bg-blue-500" : "bg-red-500"
                )}
                style={{ width: `${Math.min(bounceRateNum * 50, 100)}%` }} // Scale for better visualization
              ></div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 p-3">
          <button className="flex items-center justify-center w-full text-xs text-blue-400 hover:text-blue-300 transition-colors">
            <span className="mr-1">Manage Bounces</span>
          </button>
        </div>
      </div>

      {/* Complaint Rate Panel */}
      <div className="bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <h3 className="text-sm font-medium text-gray-200">Complaint Rate</h3>
            </div>
            <span className="text-xs text-gray-400">{timeRange}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-3xl font-bold text-white">{complaintRate}%</span>
            <div className="mt-2 flex items-center">
              <span 
                className={cn(
                  "text-xs",
                  isComplaintRateGood ? "text-green-400" : "text-red-400"
                )}
              >
                Target: &lt;0.1% (AWS Requirement)
              </span>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div 
                className={cn(
                  "h-2 rounded-full",
                  isComplaintRateGood ? "bg-yellow-500" : "bg-red-500"
                )}
                style={{ width: `${Math.min(complaintRateNum * 1000, 100)}%` }} // Scale for better visualization
              ></div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 p-3">
          <button className="flex items-center justify-center w-full text-xs text-yellow-400 hover:text-yellow-300 transition-colors">
            <span className="mr-1">View Complaints</span>
          </button>
        </div>
      </div>

      {/* Delivery Rate Panel */}
      <div className="bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <h3 className="text-sm font-medium text-gray-200">Delivery Rate</h3>
            </div>
            <span className="text-xs text-gray-400">{timeRange}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-3xl font-bold text-white">{deliveryRate}%</span>
            <div className="mt-2 flex items-center">
              <span 
                className={cn(
                  "text-xs",
                  isDeliveryRateGood ? "text-green-400" : "text-yellow-400"
                )}
              >
                Target: &gt;98% (Best Practice)
              </span>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div 
                className={cn(
                  "h-2 rounded-full bg-green-500"
                )}
                style={{ width: `${deliveryRateNum}%` }}
              ></div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 p-3">
          <button className="flex items-center justify-center w-full text-xs text-green-400 hover:text-green-300 transition-colors">
            <span className="mr-1">View Metrics</span>
          </button>
        </div>
      </div>
    </div>
  );
}
